/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: 管理偏好运动项目、配置运动参数及账号相关的设置
 * Style referenceFiles:
 * Design for: Mobile
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - 添加/移除偏好运动项目
 * - 配置各运动专属参数（时长、重量等）
 * - 点击进入修改密码页
 * - 管理员点击进入用户管理页
 * - 导航: 修改密码 (push), 用户管理 (push), 首页 (Tab)
 *
 * ## Basic Layout
 * Tab-based: Header + Scrollable Main + Pinned Bottom
 *
 * ## Reuse Components
 * - `TabBar`: 底部导航
 *
 * ## Page Layout
 * `flex flex-col h-screen bg-gray-50`
 *
 * **Header**: nav-bar - [none] - [设置] - [none]
 *
 * **Main** `flex-1 overflow-y-auto p-4 flex flex-col gap-6`:
 * 1. 偏好运动管理: `flex flex-col gap-2 bg-white rounded-xl shadow-sm p-2`
 *    - list `divide-y`
 *    - 爬楼梯 (已添加): 名称 + "移除"按钮
 *    - 平板支撑 (已添加): 名称 + "移除"按钮
 *    - 哑铃弯举 (未添加): 名称 + "添加"按钮
 * 2. 运动参数配置: `flex flex-col gap-2 bg-white rounded-xl shadow-sm p-4`
 *    - 标题 "参数配置"
 *    - 平板支撑参数: `flex flex-col gap-2 mb-4` -> 支撑时长输入框 (秒) + 休息时长输入框 (秒)
 *    - 哑铃弯举参数: `flex flex-col gap-2` -> 休息时长输入框 (秒) + 哑铃重量输入框 (kg)
 * 3. 账号与安全: `flex flex-col gap-2 bg-white rounded-xl shadow-sm p-2`
 *    - list `divide-y`
 *    - 选项: 修改密码 `flex justify-between items-center p-3` -> 标题 + 箭头图标
 *    - 选项: (仅管理员) 用户管理 `flex justify-between items-center p-3` -> 标题 + 箭头图标
 *    - 选项: 退出登录 `text-red-500 p-3 text-center`
 *
 * **Bottom**: `TabBar` - 选中项 "设置"
 *
 * ## Mock Data
 * 已添加爬楼梯、平板支撑。平板支撑：支撑60秒，休息30秒。哑铃弯举：休息60秒，重量10kg。当前用户为管理员可见用户管理入口。
 *
 * ## Reference
 * - canvas/[Feature] Settings/feature-brief.md
 * </page-design>
 */

import { useState, useRef, useEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { toast } from '@/components/function/base/Toaster'
import { pageLinks } from '../../pageLinks'
import { TabBar } from '../../components/function/TabBar'
import * as SettingsStore from '../../stores/SettingsStore'
import * as LoginStore from '../../stores/LoginStore'
import { pullFromServer, exportAllData, importData, pushAllPending, type PullConflicts, type ConflictResolver } from '@/lib/sync'
import { invalidateLocalData } from '@/lib/invalidate'
import {
  CUSTOM_ICON_CHOICES, TEMPLATES, TEMPLATE_BY_KEY,
  PRESET_EXERCISES, CATEGORY_DISPLAY,
  type PresetExercise, type PresetCategory,
} from '@/lib/exerciseRegistry'
import type { CustomExerciseTemplate, LocalCustomExercise } from '@/lib/db'
import {
  detectPlatform, isRunningStandalone, canPromptNativeInstall,
  promptInstall, onInstallStateChange, type Platform,
} from '@/lib/installPwa'
import { NumberField } from '@/components/function/NumberField'

const SECTION_BG = '#1C1F2E'
const ROW_DIVIDER = 'border-b border-[#2A2D3A]'

const MCP_API_BASE = 'https://fit-time-api.tangqiaoclaude.workers.dev'

// AI-facing prompt. Embeds the user's token so they can paste this into
// any chat UI and the model can immediately call the endpoints.
function buildAiPrompt(token: string): string {
  return `你已被授权只读访问我（黑洞健身 App 用户）的训练数据。请通过下面的 HTTP API 拉取数据，然后完成最后的"任务"部分。

Base URL: ${MCP_API_BASE}
认证: 每次请求都加 header  Authorization: Bearer ${token}
限额: 每个 Token 每天最多 50 次请求，请合理规划查询次数。

可用接口
1) GET /api/mcp/sessions/recent?days=N
   - N 为 1..365 的整数（默认 30），返回最近 N 天开始的训练记录。
2) GET /api/mcp/sessions/range?from=YYYY-MM-DD&to=YYYY-MM-DD
   - 区间跨度 <= 90 天；返回 startedAt 落在 [from 00:00 UTC, to 23:59 UTC] 的训练记录。

响应 JSON 结构
{
  "count": <number>,
  "sessions": [{
    "id": "<uuid>",
    "exerciseKey": "<稳定 key (plank/squat/...) 或 preset_<slug> 或 custom_<uuid>>",
    "exerciseType": "aerobic" | "anaerobic",
    "startedAt": "<ISO 8601 UTC>",
    "endedAt": "<ISO 8601 UTC>",
    "durationSeconds": <number>,
    "summary": <object | null>,
    "createdAt": "<ISO 8601 UTC>"
  }]
}

summary 字段语义
- 有氧 (aerobic): 可能包含 totalSeconds、laps（分段时长数组）等。
- 无氧 (anaerobic): 通常包含 sets: Array<{ reps?: number, durationSeconds?: number, weight?: number }>。
- 所有时间字段为 ISO 8601 UTC；统计周/日时按北京时间 (UTC+8)，周一为周起点。

任务
<在这里写你想让 AI 做的事，例如：分析最近 30 天的训练频率、强度和趋势，指出薄弱项并给出下周训练建议。>`
}

function Settings() {
  const { data } = useAtomValue(SettingsStore.refreshAtom)
  const addExercise = useSetAtom(SettingsStore.addExerciseAtom)
  const createCustom = useSetAtom(SettingsStore.createCustomExerciseAtom)
  const removeCustom = useSetAtom(SettingsStore.removeCustomExerciseAtom)
  const addPreset = useSetAtom(SettingsStore.addPresetExerciseAtom)
  const signOut = useSetAtom(LoginStore.signOutAtom)
  const navigate = useNavigate()

  const userExercises = data?.exercises ?? []
  const customExercises = data?.customExercises ?? []
  const user = data?.user
  const isLoggedIn = !!user
  const [showCustomModal, setShowCustomModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)

  // PWA install state
  const [platform] = useState<Platform>(() => detectPlatform())
  const [standalone, setStandalone] = useState(() => isRunningStandalone())
  const [nativePromptReady, setNativePromptReady] = useState(() => canPromptNativeInstall())
  const [showIosGuide, setShowIosGuide] = useState(false)
  useEffect(() => {
    const unsub = onInstallStateChange(() => {
      setNativePromptReady(canPromptNativeInstall())
      setStandalone(isRunningStandalone())
    })
    return () => { unsub() }
  }, [])

  const handleInstallClick = () => {
    if (platform === 'ios-safari') {
      setShowIosGuide(true)
      return
    }
    if (nativePromptReady) {
      void (async () => {
        const outcome = await promptInstall()
        if (outcome === 'accepted') toast.success('已添加到桌面')
      })()
      return
    }
    // Android/desktop Chrome but prompt not yet captured (e.g. user hasn't
    // interacted enough for Chrome to fire beforeinstallprompt). Show the
    // closest equivalent guide.
    toast.error('请稍后再试，或使用浏览器菜单中的「安装应用」')
  }

  const showInstallEntry = !standalone && platform !== 'unsupported'

  const handleSignOut = () => {
    void (async () => {
      await signOut()
      toast.success('已退出登录，本地数据保留')
    })()
  }

  // Sync / export / import
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [syncing, setSyncing] = useState(false)
  const [conflictModal, setConflictModal] = useState<{
    conflicts: PullConflicts
    resolve: (decision: 'local' | 'server') => void
  } | null>(null)

  const handlePullFromServer = () => {
    if (syncing) return
    setSyncing(true)
    void (async () => {
      try {
        const resolver: ConflictResolver = (conflicts) => new Promise((resolve) => {
          setConflictModal({ conflicts, resolve })
        })
        await pullFromServer(resolver)
        await invalidateLocalData()
        toast.success('数据已同步')
      } catch (err) {
        console.error('pull from server failed', err)
        const msg = err instanceof Error ? err.message : String(err)
        toast.error(`拉取失败: ${msg}`)
      } finally {
        setSyncing(false)
      }
    })()
  }

  const handlePushPending = () => {
    if (syncing) return
    setSyncing(true)
    void (async () => {
      try {
        await pushAllPending()
        toast.success('已推送本地待同步数据')
      } catch {
        toast.error('推送失败，请检查网络')
      } finally {
        setSyncing(false)
      }
    })()
  }

  const handleExport = () => {
    void (async () => {
      try {
        const data = await exportAllData()
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const stamp = new Date().toISOString().slice(0, 10)
        a.download = `fit-time-export-${stamp}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        toast.success(`已导出 ${data.sessions.length} 条运动记录`)
      } catch {
        toast.error('导出失败')
      }
    })()
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    void (async () => {
      try {
        const text = await file.text()
        const parsed = JSON.parse(text)
        const result = await importData(parsed)
        await invalidateLocalData()
        void pushAllPending().catch(() => {})
        toast.success(`导入成功：新增 ${result.added} 条，跳过 ${result.skipped} 条`)
      } catch (err) {
        toast.error((err as Error).message || '导入失败，文件格式无效')
      }
    })()
  }

  const handleResolveConflict = (decision: 'local' | 'server') => {
    conflictModal?.resolve(decision)
    setConflictModal(null)
  }

  // MCP API token
  const { data: tokenData, refetch: refetchToken } = useAtomValue(SettingsStore.apiTokenAtom)
  const createApiToken = useSetAtom(SettingsStore.createApiTokenAtom)
  const revokeApiToken = useSetAtom(SettingsStore.revokeApiTokenAtom)
  const [mcpLoading, setMcpLoading] = useState(false)
  const mcpToken = tokenData?.token ?? null

  const aiPrompt = mcpToken ? buildAiPrompt(mcpToken) : ''

  const handleCopyAiPrompt = () => {
    if (!aiPrompt) return
    void navigator.clipboard.writeText(aiPrompt).then(
      () => toast.success('已复制，粘贴给 AI 即可'),
      () => toast.error('复制失败'),
    )
  }

  const handleCopyToken = () => {
    if (!mcpToken) return
    void navigator.clipboard.writeText(mcpToken).then(
      () => toast.success('Token 已复制'),
      () => toast.error('复制失败'),
    )
  }

  const handleCreateToken = () => {
    if (mcpLoading) return
    setMcpLoading(true)
    void (async () => {
      try {
        await createApiToken()
        await refetchToken()
        toast.success(mcpToken ? '已生成新 Token，旧 Token 失效' : '已生成 Token')
      } catch {
        toast.error('生成失败，请检查网络')
      } finally {
        setMcpLoading(false)
      }
    })()
  }

  const handleRevokeToken = () => {
    if (mcpLoading) return
    setMcpLoading(true)
    void (async () => {
      try {
        await revokeApiToken()
        await refetchToken()
        toast.success('Token 已撤销')
      } catch {
        toast.error('撤销失败，请检查网络')
      } finally {
        setMcpLoading(false)
      }
    })()
  }

  return (
    <div className="flex flex-col h-screen relative" style={{ backgroundColor: '#12141D' }}>
      {/* Header */}
      <div className="flex-none pt-12 pb-4 px-6">
        <h1 className="text-2xl font-bold tracking-tight text-white">设置</h1>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto px-6 pb-32 flex flex-col gap-6">
        {/* Exercise Preferences — entry only; the added list lives on Home now */}
        <section>
          <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">运动项目</h2>
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
            <button
              onClick={() => setShowAddModal(true)}
              className="w-full flex items-center justify-between px-5 py-4"
            >
              <div className="flex items-center gap-3">
                <Icon icon="lucide:plus" className="w-5 h-5 text-emerald-400" />
                <span className="text-white font-medium">添加运动</span>
              </div>
              <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </section>

        {/* Weekly plan — free text per weekday, auto-save on blur */}
        <WeeklyPlanSection />

        {/* Install to Home Screen — hidden when running as installed PWA */}
        {showInstallEntry && (
          <section>
            <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">添加到桌面</h2>
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
              <button
                onClick={handleInstallClick}
                className="w-full flex items-center justify-between px-5 py-4"
              >
                <div className="flex items-center gap-3">
                  <Icon icon="lucide:smartphone-charging" className="w-5 h-5 text-emerald-400" />
                  <div className="flex flex-col items-start">
                    <span className="text-white font-medium">安装到主屏幕</span>
                    <span className="text-xs text-gray-500">像 App 一样从桌面打开</span>
                  </div>
                </div>
                <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </section>
        )}

        {/* Data Export / Import — always available */}
        <section>
          <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">数据导入导出</h2>
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
            <button
              onClick={handleExport}
              className={`w-full flex items-center justify-between px-5 py-4 ${ROW_DIVIDER}`}
            >
              <div className="flex items-center gap-3">
                <Icon icon="lucide:download" className="w-5 h-5 text-violet-400" />
                <span className="text-white font-medium">导出数据（JSON）</span>
              </div>
              <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
            </button>
            <button
              onClick={handleImportClick}
              className="w-full flex items-center justify-between px-5 py-4"
            >
              <div className="flex items-center gap-3">
                <Icon icon="lucide:upload" className="w-5 h-5 text-violet-400" />
                <span className="text-white font-medium">导入数据（JSON）</span>
              </div>
              <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleImportFile}
              className="hidden"
            />
          </div>
        </section>

        {/* Cloud Backup — guest sees "enable"; logged-in sees sync controls */}
        <section>
          <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">云端备份</h2>
          {!isLoggedIn ? (
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
              <div className="px-5 py-4 text-sm text-gray-400 leading-relaxed">
                数据当前仅保存在本设备。如果你有邀请码，可开启云端备份，让数据在更换设备时不丢失。
              </div>
              <button
                onClick={() => navigate(pageLinks.Login())}
                className="w-full flex items-center justify-between px-5 py-4 border-t border-[#2A2D3A]"
              >
                <div className="flex items-center gap-3">
                  <Icon icon="lucide:cloud-upload" className="w-5 h-5 text-emerald-400" />
                  <span className="text-white font-medium">开启云端备份</span>
                </div>
                <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          ) : (
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
              <div className={`px-5 py-3 text-xs text-gray-500 ${ROW_DIVIDER}`}>
                当前账号：<span className="text-gray-300">{user?.phone}</span>
              </div>
              <button
                onClick={handlePullFromServer}
                disabled={syncing}
                className={`w-full flex items-center justify-between px-5 py-4 ${ROW_DIVIDER} disabled:opacity-50`}
              >
                <div className="flex items-center gap-3">
                  <Icon icon="lucide:cloud-download" className="w-5 h-5 text-emerald-400" />
                  <span className="text-white font-medium">从服务器拉取</span>
                </div>
                {syncing
                  ? <Icon icon="lucide:loader-2" className="w-4 h-4 text-gray-400 animate-spin" />
                  : <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />}
              </button>
              <button
                onClick={handlePushPending}
                disabled={syncing}
                className="w-full flex items-center justify-between px-5 py-4 disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <Icon icon="lucide:cloud-upload" className="w-5 h-5 text-emerald-400" />
                  <span className="text-white font-medium">推送待同步数据</span>
                </div>
                <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
        </section>

        {/* MCP / Agent Access — only when logged in */}
        {isLoggedIn && (
          <section>
            <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">MCP 接口</h2>
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
              <div className={`px-5 py-3 text-xs text-gray-500 leading-relaxed ${ROW_DIVIDER}`}>
                给 AI Agent 只读访问你的运动数据。每个账号同时只能有 1 个 Token，每日最多 50 次调用。
              </div>

              {mcpToken ? (
                <>
                  <div className={`px-5 py-4 ${ROW_DIVIDER}`}>
                    <div className="text-[12px] text-gray-300 leading-relaxed mb-2">
                      把下面这段说明复制给 AI 即可。AI 收到后会用其中的 Token 调用接口拉取你的运动数据。
                    </div>
                    <pre
                      className="mt-2 p-3 rounded bg-[#0F1119] font-mono text-[10px] text-gray-300 leading-snug whitespace-pre-wrap max-h-48 overflow-y-auto"
                      onClick={(e) => {
                        const range = document.createRange()
                        range.selectNodeContents(e.currentTarget)
                        const sel = window.getSelection()
                        sel?.removeAllRanges()
                        sel?.addRange(range)
                      }}
                    >
                      {aiPrompt}
                    </pre>
                    <button
                      onClick={handleCopyAiPrompt}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-sm"
                    >
                      <Icon icon="lucide:copy" className="w-4 h-4" />
                      复制给 AI
                    </button>
                  </div>

                  <button
                    onClick={handleCopyToken}
                    className={`w-full flex items-center justify-between px-5 py-4 ${ROW_DIVIDER}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon icon="lucide:key-round" className="w-5 h-5 text-gray-400" />
                      <span className="text-white font-medium text-sm">仅复制 Token</span>
                    </div>
                    <span className="font-mono text-[10px] text-gray-500 truncate max-w-[160px]">{mcpToken.slice(0, 12)}…</span>
                  </button>
                  <button
                    onClick={handleCreateToken}
                    disabled={mcpLoading}
                    className={`w-full flex items-center justify-between px-5 py-4 ${ROW_DIVIDER} disabled:opacity-50`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon icon="lucide:refresh-cw" className="w-5 h-5 text-gray-400" />
                      <span className="text-white font-medium text-sm">重新生成（旧 Token 失效）</span>
                    </div>
                  </button>
                  <button
                    onClick={handleRevokeToken}
                    disabled={mcpLoading}
                    className="w-full text-left px-5 py-4 text-red-400 font-medium text-sm disabled:opacity-50"
                  >
                    撤销 Token
                  </button>
                </>
              ) : (
                <button
                  onClick={handleCreateToken}
                  disabled={mcpLoading}
                  className="w-full flex items-center justify-between px-5 py-4 disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <Icon icon="lucide:key-round" className="w-5 h-5 text-emerald-400" />
                    <span className="text-white font-medium">生成 API Token</span>
                  </div>
                </button>
              )}
            </div>
          </section>
        )}

        {/* Account & Security — only when logged in */}
        {isLoggedIn && (
          <section>
            <h2 className="text-sm font-bold text-gray-500 mb-3 px-1 uppercase tracking-wider">账号与安全</h2>
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
              <button
                onClick={() => navigate(pageLinks.ChangePassword())}
                className={`w-full flex items-center justify-between px-5 py-4 ${ROW_DIVIDER}`}
              >
                <span className="text-white font-medium">修改密码</span>
                <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate(pageLinks.UserManagement())}
                  className={`w-full flex items-center justify-between px-5 py-4 ${ROW_DIVIDER}`}
                >
                  <span className="text-white font-medium">用户管理</span>
                  <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-500" />
                </button>
              )}
              <button
                onClick={handleSignOut}
                className="w-full py-4 text-center text-red-400 font-bold"
              >
                退出登录
              </button>
            </div>
          </section>
        )}

        {/* Footer: author info + version. Version tap opens the changelog
            (deliberately discreet). Blog link opens in a new tab / external
            browser when launched as a PWA. */}
        <div className="mt-4 mb-4 flex flex-col items-center gap-1">
          <p className="text-[10px] text-gray-600">
            作者：唐巧 · blog:{' '}
            <a
              href="https://devtang.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-500 underline"
            >
              https://devtang.com
            </a>
          </p>
          <button
            onClick={() => navigate(pageLinks.ChangeLog())}
            className="text-[10px] text-gray-600 font-mono"
          >
            版本: {__APP_VERSION__}
          </button>
        </div>
      </main>

      {/* Conflict resolution modal */}
      {conflictModal && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 px-4 pb-8">
          <div
            className="w-full max-w-md rounded-2xl p-5 flex flex-col gap-4"
            style={{ backgroundColor: '#1C1F2E' }}
          >
            <div className="flex items-center gap-2">
              <Icon icon="lucide:alert-triangle" className="w-5 h-5 text-amber-400" />
              <h3 className="text-white font-bold">数据冲突</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              本地有未同步的更改，与服务器数据不一致。保留哪一边？
            </p>
            <div className="flex flex-col gap-2 text-xs text-gray-500 bg-[#12141D] rounded-lg p-3 max-h-48 overflow-y-auto">
              {conflictModal.conflicts.exercises && (
                <div>
                  <div className="text-gray-300 font-medium mb-1">运动项目</div>
                  <div>本地：{conflictModal.conflicts.exercises.local.join('、') || '（无）'}</div>
                  <div>服务器：{conflictModal.conflicts.exercises.server.join('、') || '（无）'}</div>
                </div>
              )}
              {conflictModal.conflicts.settings.length > 0 && (
                <div>
                  <div className="text-gray-300 font-medium mb-1 mt-2">运动参数</div>
                  <div>{conflictModal.conflicts.settings.map(c => c.exerciseKey).join('、')}</div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleResolveConflict('local')}
                className="flex-1 py-3 rounded-xl bg-violet-400/10 text-violet-400 font-bold text-sm"
              >
                保留本地
              </button>
              <button
                onClick={() => handleResolveConflict('server')}
                className="flex-1 py-3 rounded-xl bg-emerald-400/10 text-emerald-400 font-bold text-sm"
              >
                用服务器
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add exercise modal — preset + own custom + new custom entry */}
      {showAddModal && (
        <AddExerciseModal
          onClose={() => setShowAddModal(false)}
          addedKeys={userExercises}
          customRows={customExercises}
          onAddPreset={async (key) => { await addPreset(key); toast.success('已添加') }}
          onReAddCustom={async (key) => { await addExercise(key); toast.success('已添加') }}
          onDeleteCustom={async (key, name) => {
            if (confirm(`彻底删除"${name}"？历史训练记录会保留。`)) {
              await removeCustom(key)
              toast.success('已删除')
            }
          }}
          onOpenCreateCustom={() => {
            setShowAddModal(false)
            setShowCustomModal(true)
          }}
        />
      )}

      {/* iOS install guide */}
      {showIosGuide && (
        <IosInstallGuide onClose={() => setShowIosGuide(false)} />
      )}

      {/* Create custom exercise modal */}
      {showCustomModal && (
        <CreateCustomExerciseModal
          onClose={() => setShowCustomModal(false)}
          onCreate={async (params) => {
            try {
              await createCustom(params)
              setShowCustomModal(false)
              toast.success('已添加自定义运动')
            } catch (e) {
              toast.error((e as Error).message || '添加失败')
            }
          }}
        />
      )}

      {/* TabBar */}
      <TabBar />
    </div>
  )
}

// ============================================================================
// Add exercise modal — picker with search + recommended + own customs
// ============================================================================

interface AddModalProps {
  onClose: () => void
  addedKeys: string[]
  customRows: LocalCustomExercise[]
  onAddPreset: (key: string) => Promise<void>
  onReAddCustom: (key: string) => Promise<void>
  onDeleteCustom: (key: string, name: string) => Promise<void>
  onOpenCreateCustom: () => void
}

interface PickerItem {
  exerciseKey: string
  label: string
  icon: string
  exerciseType: 'aerobic' | 'anaerobic'
  source: 'preset' | 'custom'
  category?: PresetCategory   // only for source='preset'
}

function AddExerciseModal({
  onClose, addedKeys, customRows,
  onAddPreset, onReAddCustom, onDeleteCustom, onOpenCreateCustom,
}: AddModalProps) {
  const [search, setSearch] = useState('')
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const addedSet = new Set(addedKeys)

  const recommended: PickerItem[] = PRESET_EXERCISES
    .filter((e: PresetExercise) => !addedSet.has(e.exerciseKey))
    .map((e): PickerItem => ({
      exerciseKey: e.exerciseKey, label: e.name, icon: e.icon,
      exerciseType: e.exerciseType, source: 'preset', category: e.category,
    }))

  const myCustoms: PickerItem[] = customRows
    .filter(c => c.exerciseKey.startsWith('custom_') && !addedSet.has(c.exerciseKey))
    .map((c): PickerItem => ({
      exerciseKey: c.exerciseKey, label: c.name, icon: c.icon, exerciseType: c.exerciseType, source: 'custom',
    }))

  const q = search.trim().toLowerCase()
  const filter = (items: PickerItem[]) =>
    q ? items.filter(i => i.label.toLowerCase().includes(q)) : items
  const filteredRecommended = filter(recommended)
  const filteredCustoms = filter(myCustoms)

  // Group recommended by category (only when not searching)
  const recommendedByCategory: Array<{ key: PresetCategory; label: string; items: PickerItem[] }> = q
    ? []
    : CATEGORY_DISPLAY
        .map(c => ({
          key: c.key,
          label: c.label,
          items: filteredRecommended.filter(i => i.category === c.key),
        }))
        .filter(g => g.items.length > 0)

  const handleAdd = async (item: PickerItem) => {
    if (busyKey) return
    setBusyKey(item.exerciseKey)
    try {
      if (item.source === 'preset') await onAddPreset(item.exerciseKey)
      else await onReAddCustom(item.exerciseKey)
    } finally {
      setBusyKey(null)
    }
  }

  const renderRow = (item: PickerItem, idx: number, total: number) => {
    const colorClass = item.exerciseType === 'aerobic' ? 'text-emerald-400' : 'text-violet-400'
    return (
      <div
        key={item.exerciseKey}
        className={`flex items-center justify-between px-5 py-3.5 ${idx < total - 1 ? 'border-b border-[#2A2D3A]' : ''}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Icon icon={item.icon} className={`w-5 h-5 ${colorClass} flex-none`} />
          <span className="text-white font-medium truncate">{item.label}</span>
          <span className={`text-xs font-bold ${colorClass} flex-none`}>
            {item.exerciseType === 'aerobic' ? '有氧' : '无氧'}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-none">
          {item.source === 'custom' && (
            <button
              onClick={() => void onDeleteCustom(item.exerciseKey, item.label)}
              aria-label="彻底删除"
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500"
              style={{ backgroundColor: '#12141D' }}
            >
              <Icon icon="lucide:trash-2" className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => void handleAdd(item)}
            disabled={busyKey === item.exerciseKey}
            className="px-3 py-1.5 rounded-xl text-sm font-bold text-emerald-400 bg-emerald-400/10 disabled:opacity-60"
          >
            添加
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60">
      <div className="w-full max-w-md rounded-t-3xl flex flex-col max-h-[90vh]" style={{ backgroundColor: '#1C1F2E' }}>
        {/* Header — sticky */}
        <div className="flex-none p-5 pb-3 flex flex-col gap-3" style={{ backgroundColor: '#1C1F2E' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold text-lg">添加运动</h3>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#12141D' }}>
              <Icon icon="lucide:x" className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          {/* Search */}
          <div className="relative">
            <Icon icon="lucide:search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索运动名称"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-white outline-none"
              style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
            />
          </div>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 flex flex-col gap-4">
          {/* Search mode: flat list */}
          {q && filteredRecommended.length > 0 && (
            <section>
              <h4 className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">推荐运动</h4>
              <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#12141D' }}>
                {filteredRecommended.map((item, idx) => renderRow(item, idx, filteredRecommended.length))}
              </div>
            </section>
          )}

          {/* Browse mode: grouped by category */}
          {!q && recommendedByCategory.map(group => (
            <section key={group.key}>
              <h4 className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">{group.label}</h4>
              <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#12141D' }}>
                {group.items.map((item, idx) => renderRow(item, idx, group.items.length))}
              </div>
            </section>
          ))}

          {/* My customs */}
          {filteredCustoms.length > 0 && (
            <section>
              <h4 className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">我的自定义</h4>
              <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#12141D' }}>
                {filteredCustoms.map((item, idx) => renderRow(item, idx, filteredCustoms.length))}
              </div>
            </section>
          )}

          {/* Empty search result */}
          {filteredRecommended.length === 0 && filteredCustoms.length === 0 && (
            <div className="py-6 text-center text-sm text-gray-500">
              {q ? `没找到匹配「${search}」的运动` : '所有运动都已添加'}
            </div>
          )}

          {/* New custom entry — always at bottom */}
          <button
            onClick={onOpenCreateCustom}
            className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl border-2 border-dashed border-[#2A2D3A]"
          >
            <Icon icon="lucide:plus" className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-bold">新建自定义运动</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// iOS install guide modal
// ============================================================================

function IosInstallGuide({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60">
      <div className="w-full max-w-md rounded-t-3xl p-5 flex flex-col gap-5" style={{ backgroundColor: '#1C1F2E' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">添加到 iPhone 主屏幕</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#12141D' }}>
            <Icon icon="lucide:x" className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <p className="text-sm text-gray-400 leading-relaxed">
          iOS Safari 需要手动操作（系统限制无法一键安装）：
        </p>
        <ol className="flex flex-col gap-3">
          <li className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: '#12141D' }}>
            <span className="w-7 h-7 rounded-full flex items-center justify-center bg-emerald-400/10 text-emerald-400 font-bold text-sm flex-none">1</span>
            <span className="text-sm text-gray-200 flex-1">点击底部工具栏的</span>
            <Icon icon="lucide:share" className="w-5 h-5 text-emerald-400 flex-none" />
            <span className="text-sm text-gray-200">分享</span>
          </li>
          <li className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: '#12141D' }}>
            <span className="w-7 h-7 rounded-full flex items-center justify-center bg-emerald-400/10 text-emerald-400 font-bold text-sm flex-none">2</span>
            <span className="text-sm text-gray-200 flex-1">向下滚动，选择</span>
            <Icon icon="lucide:plus-square" className="w-5 h-5 text-emerald-400 flex-none" />
            <span className="text-sm text-gray-200">添加到主屏幕</span>
          </li>
          <li className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: '#12141D' }}>
            <span className="w-7 h-7 rounded-full flex items-center justify-center bg-emerald-400/10 text-emerald-400 font-bold text-sm flex-none">3</span>
            <span className="text-sm text-gray-200">右上角点「添加」即可</span>
          </li>
        </ol>
        <p className="text-xs text-gray-500 leading-relaxed">
          注意：如果当前不是用 Safari 打开，请先在 Safari 中访问本页面。Chrome / Firefox 等其他 iOS 浏览器无法安装。
        </p>
        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl text-white font-bold text-sm"
          style={{ backgroundColor: '#10B981', borderBottom: '4px solid #059669' }}
        >
          知道了
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Create custom exercise modal
// ============================================================================

interface CreateModalProps {
  onClose: () => void
  onCreate: (params: SettingsStore.CreateCustomExerciseParams) => Promise<void>
}

function CreateCustomExerciseModal({ onClose, onCreate }: CreateModalProps) {
  const [name, setName] = useState('')
  const [template, setTemplate] = useState<CustomExerciseTemplate>('manual_reps')
  const [icon, setIcon] = useState<string>(CUSTOM_ICON_CHOICES[0])
  const templateInfo = TEMPLATE_BY_KEY[template]
  const [restDuration, setRestDuration] = useState<number>(templateInfo.defaultRest)
  const [defaultDuration, setDefaultDuration] = useState<number>(templateInfo.defaultDuration ?? 60)
  const [weight, setWeight] = useState<number>(templateInfo.defaultWeight ?? 0)
  const initialQuickReps = templateInfo.defaultQuickReps ?? [10, 15, 20]
  const [quickReps1, setQuickReps1] = useState<number>(initialQuickReps[0])
  const [quickReps2, setQuickReps2] = useState<number>(initialQuickReps[1])
  const [quickReps3, setQuickReps3] = useState<number>(initialQuickReps[2])
  const [submitting, setSubmitting] = useState(false)

  const handleTemplate = (t: CustomExerciseTemplate) => {
    setTemplate(t)
    const info = TEMPLATE_BY_KEY[t]
    setRestDuration(info.defaultRest)
    if (info.defaultDuration !== undefined) setDefaultDuration(info.defaultDuration)
    if (info.defaultWeight !== undefined) setWeight(info.defaultWeight)
    if (info.defaultQuickReps) {
      setQuickReps1(info.defaultQuickReps[0])
      setQuickReps2(info.defaultQuickReps[1])
      setQuickReps3(info.defaultQuickReps[2])
    }
  }

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('请输入运动名称')
      return
    }
    setSubmitting(true)
    void (async () => {
      const isManualReps = template === 'manual_reps'
      const info = TEMPLATE_BY_KEY[template]
      await onCreate({
        name: name.trim(),
        icon,
        template,
        exerciseType: info.defaultType,
        restDuration: info.needsRestDuration ? restDuration : null,
        defaultDuration: info.needsDefaultDuration ? defaultDuration : null,
        weight: info.supportsWeight ? weight : null,
        quickReps1: isManualReps ? quickReps1 : null,
        quickReps2: isManualReps ? quickReps2 : null,
        quickReps3: isManualReps ? quickReps3 : null,
      })
      setSubmitting(false)
    })()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60">
      <div className="w-full max-w-md rounded-t-3xl p-5 flex flex-col gap-5 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#1C1F2E' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">新建自定义运动</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#12141D' }}>
            <Icon icon="lucide:x" className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Name */}
        <div className="flex flex-col gap-2">
          <label className="text-xs text-gray-500 px-1">名称</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={20}
            placeholder="例如：跳绳"
            className="w-full px-4 py-3 rounded-xl text-base text-white outline-none"
            style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
          />
        </div>

        {/* Template */}
        <div className="flex flex-col gap-2">
          <label className="text-xs text-gray-500 px-1">模板</label>
          <div className="flex flex-col gap-2">
            {TEMPLATES.map(t => {
              const active = t.template === template
              return (
                <button
                  key={t.template}
                  onClick={() => handleTemplate(t.template)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${active ? 'border-2 border-emerald-400' : 'border-2 border-transparent'}`}
                  style={{ backgroundColor: '#12141D' }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{t.label}</span>
                    <span className={`text-xs font-bold ${t.defaultType === 'aerobic' ? 'text-emerald-400' : 'text-violet-400'}`}>
                      {t.defaultType === 'aerobic' ? '有氧' : '无氧'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-relaxed">{t.description}</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Rest duration (only when applicable) */}
        {templateInfo.needsRestDuration && (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-500 px-1">组间休息时长（秒）</label>
            <NumberField
              value={restDuration}
              onChange={setRestDuration}
              min={0}
              fallbackOnBlur={templateInfo.defaultRest}
              className="w-full px-4 py-3 rounded-xl text-base text-white outline-none"
              style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
            />
          </div>
        )}

        {/* Default per-set duration (countdown only) */}
        {templateInfo.needsDefaultDuration && (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-500 px-1">单组默认时长（秒）</label>
            <NumberField
              value={defaultDuration}
              onChange={setDefaultDuration}
              min={5}
              fallbackOnBlur={templateInfo.defaultDuration ?? 60}
              className="w-full px-4 py-3 rounded-xl text-base text-white outline-none"
              style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
            />
          </div>
        )}

        {/* Weight (sets_with_rest only) */}
        {templateInfo.supportsWeight && (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-500 px-1">默认重量（kg，自重填 0）</label>
            <NumberField
              value={weight}
              onChange={setWeight}
              min={0}
              fallbackOnBlur={templateInfo.defaultWeight ?? 0}
              className="w-full px-4 py-3 rounded-xl text-base text-white outline-none"
              style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
            />
          </div>
        )}

        {/* Quick reps (only for manual_reps) */}
        {template === 'manual_reps' && (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-500 px-1">快捷次数（3 个一键填入按钮）</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: quickReps1, set: setQuickReps1, fb: initialQuickReps[0] },
                { value: quickReps2, set: setQuickReps2, fb: initialQuickReps[1] },
                { value: quickReps3, set: setQuickReps3, fb: initialQuickReps[2] },
              ].map((q, i) => (
                <NumberField
                  key={i}
                  value={q.value}
                  onChange={q.set}
                  min={1}
                  fallbackOnBlur={q.fb}
                  className="w-full px-3 py-3 rounded-xl text-base text-white text-center outline-none"
                  style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Icon */}
        <div className="flex flex-col gap-2">
          <label className="text-xs text-gray-500 px-1">图标</label>
          <div className="grid grid-cols-6 gap-2">
            {CUSTOM_ICON_CHOICES.map(iconKey => {
              const active = iconKey === icon
              return (
                <button
                  key={iconKey}
                  onClick={() => setIcon(iconKey)}
                  className={`aspect-square rounded-xl flex items-center justify-center transition-all ${active ? 'border-2 border-emerald-400' : 'border-2 border-transparent'}`}
                  style={{ backgroundColor: '#12141D' }}
                >
                  <Icon icon={iconKey} className={`w-5 h-5 ${active ? 'text-emerald-400' : 'text-gray-300'}`} />
                </button>
              )
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 pb-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-3 rounded-xl text-gray-400 font-bold text-sm"
            style={{ backgroundColor: '#12141D' }}
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-60"
            style={{ backgroundColor: '#10B981', borderBottom: '4px solid #059669' }}
          >
            {submitting ? '添加中…' : '添加并加到首页'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Weekly plan section — 7 text inputs, auto-save on blur
// ============================================================================

const WEEKDAY_LABELS: Array<{ key: SettingsStore.WeekdayKey; label: string }> = [
  { key: 'mon', label: '周一' },
  { key: 'tue', label: '周二' },
  { key: 'wed', label: '周三' },
  { key: 'thu', label: '周四' },
  { key: 'fri', label: '周五' },
  { key: 'sat', label: '周六' },
  { key: 'sun', label: '周日' },
]

function WeeklyPlanSection() {
  const { data: plan } = useAtomValue(SettingsStore.weeklyPlanAtom)
  const updateDay = useSetAtom(SettingsStore.updateWeeklyPlanDayAtom)
  const [isEditing, setIsEditing] = useState(false)

  return (
    <section>
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">运动计划</h2>
        <button
          onClick={() => setIsEditing(!isEditing)}
          aria-label={isEditing ? '完成' : '修改'}
          className="w-7 h-7 rounded-full flex items-center justify-center"
          style={{ backgroundColor: isEditing ? '#10B981' : '#1C1F2E' }}
        >
          <Icon
            icon={isEditing ? 'lucide:check' : 'lucide:pencil-line'}
            className={`w-3.5 h-3.5 ${isEditing ? 'text-white' : 'text-gray-400'}`}
          />
        </button>
      </div>
      <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: SECTION_BG }}>
        {WEEKDAY_LABELS.map((d, idx) => {
          const isLast = idx === WEEKDAY_LABELS.length - 1
          const text = plan?.[d.key] ?? null
          return isEditing ? (
            <WeekdayEditRow
              key={d.key}
              label={d.label}
              initial={text}
              onCommit={(next) => void updateDay({ day: d.key, text: next })}
              isLast={isLast}
            />
          ) : (
            <WeekdayDisplayRow key={d.key} label={d.label} text={text} isLast={isLast} />
          )
        })}
      </div>
    </section>
  )
}

function WeekdayDisplayRow({ label, text, isLast }: {
  label: string
  text: string | null
  isLast: boolean
}) {
  return (
    <div className={`flex items-center gap-3 px-5 py-3 ${isLast ? '' : ROW_DIVIDER}`}>
      <span className="text-sm text-gray-400 font-medium w-10 flex-none">{label}</span>
      <span className={`text-sm flex-1 truncate ${text ? 'text-white' : 'text-gray-600'}`}>
        {text ?? '休息'}
      </span>
    </div>
  )
}

function WeekdayEditRow({ label, initial, onCommit, isLast }: {
  label: string
  initial: string | null
  onCommit: (text: string | null) => void
  isLast: boolean
}) {
  const [draft, setDraft] = useState(initial ?? '')
  useEffect(() => { setDraft(initial ?? '') }, [initial])

  const handleBlur = () => {
    const trimmed = draft.trim()
    const next = trimmed.length > 0 ? trimmed : null
    if (next !== initial) onCommit(next)
  }

  return (
    <div className={`flex items-center gap-3 px-5 py-3 ${isLast ? '' : ROW_DIVIDER}`}>
      <span className="text-sm text-gray-400 font-medium w-10 flex-none">{label}</span>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        placeholder="休息"
        maxLength={100}
        className="flex-1 px-3 py-2 rounded-lg text-sm text-white outline-none"
        style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
      />
    </div>
  )
}

export default Settings
